/* eslint-disable react-refresh/only-export-components */
import React, {
  createContext,
  useState,
  useEffect,
  useContext,
  useCallback,
  ReactNode,
} from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { useQueryClient } from "@tanstack/react-query";
import { API_BASE_URL } from "../services/common/constants";
import {
  MyHotelInfo,
  HotelAccessDto,
  RegistrationStatusDto,
  PlatformHotelSearchResult,
} from "../services/admin/types/account.types";
import { serviceRegistry } from "../services/core/serviceRegistry";
import { AccountService } from "../services/admin/services/accountService";
import { logger } from "../utils/logger";
import { useUnifiedNotificationStore } from "../stores/unifiedNotificationStore";
import { HOTEL_STATE_INVALIDATED_EVENT } from "../utils/hotelStateEvents";

// localStorage key for selected hotel (shared across all tabs)
const SELECTED_HOTEL_KEY = "selectedHotelId";

export type SwitchStage = 'idle' | 'preparing' | 'loading' | 'finishing';

interface HotelContextType {
  myHotelInfo: MyHotelInfo | null;
  availableHotels: HotelAccessDto[];
  isHotelInfoLoading: boolean;
  /** True once the initial hotel info fetch has completed (success or error) */
  hasCompletedInitialFetch: boolean;
  isSwitching: boolean;
  switchStage: SwitchStage;
  hasMultipleHotels: boolean;
  isPlatformAdmin: boolean;
  registrationStatus: RegistrationStatusDto | null;
  /** Error message from the last hotel info fetch, null if successful */
  fetchError: string | null;
  /** Whether the last fetch error was a 401 auth error */
  isAuthError: boolean;
  refreshHotelInfo: () => Promise<MyHotelInfo | null>;
  refreshAvailableHotels: () => Promise<HotelAccessDto[]>;
  refreshRegistrationStatus: () => Promise<RegistrationStatusDto | null>;
  selectHotel: (hotelId: string) => Promise<void>;
  searchAllHotels: (search?: string) => Promise<PlatformHotelSearchResult[]>;
  getSelectedHotelId: () => string | null;
}

const HotelContext = createContext<HotelContextType | undefined>(undefined);

export const useHotel = (): HotelContextType => {
  const context = useContext(HotelContext);
  // Be resilient when used in portals or before provider mounts
  if (!context) {
    return {
      myHotelInfo: null,
      availableHotels: [],
      isHotelInfoLoading: true,
      hasCompletedInitialFetch: false,
      isSwitching: false,
      switchStage: 'idle',
      hasMultipleHotels: false,
      isPlatformAdmin: false,
      registrationStatus: null,
      fetchError: null,
      isAuthError: false,
      refreshHotelInfo: async () => null,
      refreshAvailableHotels: async () => [],
      refreshRegistrationStatus: async () => null,
      selectHotel: async () => {},
      searchAllHotels: async () => [],
      getSelectedHotelId: () => null,
    } as HotelContextType;
  }
  return context;
};

interface HotelProviderProps {
  children: ReactNode;
}

export const HotelProvider: React.FC<HotelProviderProps> = ({ children }) => {
  const { getAccessTokenSilently, isAuthenticated, isLoading, user } =
    useAuth0();
  const queryClient = useQueryClient();
  const [myHotelInfo, setMyHotelInfo] = useState<MyHotelInfo | null>(null);
  const [availableHotels, setAvailableHotels] = useState<HotelAccessDto[]>([]);
  const [isHotelInfoLoading, setIsHotelInfoLoading] = useState<boolean>(true);
  const [isSwitching, setIsSwitching] = useState<boolean>(false);
  const [switchStage, setSwitchStage] = useState<SwitchStage>('idle');
  const [registrationStatus, setRegistrationStatus] = useState<RegistrationStatusDto | null>(null);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [isAuthError, setIsAuthError] = useState<boolean>(false);
  const [hasCompletedInitialFetch, setHasCompletedInitialFetch] = useState<boolean>(false);
  const [accountService, setAccountService] = useState<AccountService | null>(
    null,
  );

  // Get selected hotel ID from localStorage
  const getSelectedHotelId = useCallback((): string | null => {
    return localStorage.getItem(SELECTED_HOTEL_KEY);
  }, []);

  // Set selected hotel ID in localStorage
  const setSelectedHotelId = useCallback((hotelId: string): void => {
    localStorage.setItem(SELECTED_HOTEL_KEY, hotelId);
  }, []);

  // Initialize service registry and services when auth is ready
  useEffect(() => {
    if (isAuthenticated && !isLoading && getAccessTokenSilently) {
      // Initialize service registry if not already initialized
      if (!serviceRegistry.isAdminInitialized()) {
        serviceRegistry.initializeAdmin({
          baseUrl: API_BASE_URL,
          getAccessToken: getAccessTokenSilently,
        });
      }

      // Initialize account service only if not already created.
      // Auth0 silent auth can re-trigger this effect without the client changing.
      // Re-creating AccountService would cascade into data-fetch effects.
      setAccountService((prev) => {
        if (prev) return prev;
        const client = serviceRegistry.getAdminClient();
        return client ? new AccountService(client) : null;
      });
    }
  }, [isAuthenticated, isLoading, getAccessTokenSilently]);

  // Function to fetch available hotels
  const fetchAvailableHotels = useCallback(async (): Promise<
    HotelAccessDto[]
  > => {
    if (!accountService) return [];
    try {
      const result = await accountService.getMyHotels();
      if (result.success) {
        setAvailableHotels(result.data);

        // If user has hotels but no selection, auto-select first one
        const currentSelection = getSelectedHotelId();
        if (result.data.length > 0 && !currentSelection) {
          // Prefer owned hotel, then earliest joined
          const defaultHotel =
            result.data.find((h) => h.isOwner) || result.data[0];
          setSelectedHotelId(defaultHotel.hotelId);
        }

        return result.data;
      }
      return [];
    } catch (error) {
      logger.error("HotelContext: Error fetching available hotels:", error);
      return [];
    }
  }, [accountService, getSelectedHotelId, setSelectedHotelId]);

  // Function to fetch registration status
  const fetchRegistrationStatus = useCallback(async (): Promise<RegistrationStatusDto | null> => {
    if (!accountService) return null;
    try {
      const result = await accountService.getRegistrationStatus();
      if (result.success) {
        setRegistrationStatus(result.data);
        return result.data;
      }
      return null;
    } catch (error) {
      logger.error("HotelContext: Error fetching registration status:", error);
      return null;
    }
  }, [accountService]);

  // Function to fetch hotel info
  const fetchHotelInfo = useCallback(async (): Promise<MyHotelInfo | null> => {
    if (!accountService) return null;
    setIsHotelInfoLoading(true);
    setFetchError(null);
    setIsAuthError(false);
    try {
      const result = await accountService.getMyHotelInfo();
      if (result.success) {
        setMyHotelInfo(result.data);
        setFetchError(null);
        setIsAuthError(false);

        // Ensure localStorage is in sync with what backend returns
        if (result.data.hotelId) {
          const currentSelection = getSelectedHotelId();
          if (!currentSelection) {
            setSelectedHotelId(result.data.hotelId);
          }
        }

        return result.data;
      }
      // API returned an error response
      const isNoHotelError = result.error?.status === 404 ||
        (result.error?.status === 400 && result.error?.message?.includes("hotel information could not be determined"));
      if (isNoHotelError) {
        // No hotel - not an error, just no hotel linked yet
        setMyHotelInfo(null);
        setFetchError(null);
      } else {
        // Real error (401, 403, 500, etc.)
        setIsAuthError(result.error?.status === 401);
        setFetchError(result.error?.message || "Failed to load hotel information");
      }
      return null;
    } catch (error) {
      logger.error("HotelContext: Error fetching hotel info:", error);
      setFetchError("Unable to connect to the server. Please check your connection.");
      return null;
    } finally {
      setIsHotelInfoLoading(false);
      setHasCompletedInitialFetch(true);
    }
  }, [accountService, getSelectedHotelId, setSelectedHotelId]);

  // Search all hotels (platform admin only)
  const searchAllHotels = useCallback(
    async (search?: string): Promise<PlatformHotelSearchResult[]> => {
      if (!accountService) return [];
      try {
        const result = await accountService.searchAllHotels(search, 20);
        if (result.success) {
          return result.data;
        }
        return [];
      } catch (error) {
        logger.error("HotelContext: Error searching all hotels:", error);
        return [];
      }
    },
    [accountService],
  );

  // Get reset function from notification store
  const resetNotifications = useUnifiedNotificationStore((state) => state.reset);

  // Select a different hotel (with loading animation)
  const selectHotel = useCallback(
    async (hotelId: string): Promise<void> => {
      if (!accountService) return;

      const currentHotelId = myHotelInfo?.hotelId;
      if (hotelId === currentHotelId) return; // Already selected

      logger.info(`HotelContext: Switching hotel from ${currentHotelId} to ${hotelId}`);
      setIsSwitching(true);
      setSwitchStage('preparing');

      try {
        // Stage 1: Preparing - Cancel pending queries, clear cache, reset notifications, and update selection
        queryClient.cancelQueries({ queryKey: ['notificationSummary'] });
        queryClient.clear();
        resetNotifications();
        setSelectedHotelId(hotelId);
        await new Promise((resolve) => setTimeout(resolve, 800));

        // Stage 2: Loading - Fetch new hotel info
        setSwitchStage('loading');
        await fetchHotelInfo();
        await new Promise((resolve) => setTimeout(resolve, 600));

        // Stage 3: Finishing - Final setup
        setSwitchStage('finishing');
        await new Promise((resolve) => setTimeout(resolve, 500));
      } catch (error) {
        logger.error("HotelContext: Error switching hotel:", error);
      } finally {
        setSwitchStage('idle');
        setIsSwitching(false);
      }
    },
    [
      accountService,
      myHotelInfo?.hotelId,
      queryClient,
      setSelectedHotelId,
      fetchHotelInfo,
      resetNotifications,
    ],
  );

  // Effect to fetch data when auth state is ready
  useEffect(() => {
    if (isLoading) {
      setIsHotelInfoLoading(true);
      setHasCompletedInitialFetch(false);
      return;
    }

    if (!isAuthenticated) {
      setMyHotelInfo(null);
      setAvailableHotels([]);
      setRegistrationStatus(null);
      setFetchError(null);
      setIsAuthError(false);
      setIsHotelInfoLoading(false);
      // Auth is resolved and we know there is no signed-in user, so admin routing
      // can proceed to the unauthenticated no-hotels landing flow.
      setHasCompletedInitialFetch(true);
      return;
    }

    if (!user) {
      return;
    }

    if (accountService) {
      // Fetch hotel info, available hotels, and registration status
      fetchHotelInfo();
      fetchAvailableHotels();
      fetchRegistrationStatus();
    }
  }, [isLoading, isAuthenticated, user, accountService, fetchHotelInfo, fetchAvailableHotels, fetchRegistrationStatus]);

  // Auto-refresh on any successful admin mutation that may have changed
  // hotel/user state. Dispatched by apiClient's response interceptor after
  // POST/PUT/PATCH/DELETE to /api/admin/registration/* or /api/admin/account/*.
  // Guards consumers from forgetting to call refreshHotelInfo() manually.
  useEffect(() => {
    if (!accountService) return;
    const handler = () => {
      fetchHotelInfo();
      fetchAvailableHotels();
      fetchRegistrationStatus();
    };
    window.addEventListener(HOTEL_STATE_INVALIDATED_EVENT, handler);
    return () => window.removeEventListener(HOTEL_STATE_INVALIDATED_EVENT, handler);
  }, [accountService, fetchHotelInfo, fetchAvailableHotels, fetchRegistrationStatus]);

  // Listen for storage changes from other tabs
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === SELECTED_HOTEL_KEY && e.newValue) {
        const newHotelId = e.newValue;
        const currentHotelId = myHotelInfo?.hotelId;

        if (currentHotelId && newHotelId !== currentHotelId) {
          logger.info(
            `HotelContext: Hotel changed in another tab to ${newHotelId}`,
          );
          // Clear cache, reset notifications, and refresh data
          queryClient.clear();
          resetNotifications();
          fetchHotelInfo();
        }
      }
    };

    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, [myHotelInfo?.hotelId, queryClient, fetchHotelInfo, resetNotifications]);

  // Handle tab visibility changes - sync hotel when tab becomes visible
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible" && !isSwitching) {
        const storedHotelId = getSelectedHotelId();
        const currentHotelId = myHotelInfo?.hotelId;

        // If localStorage has a different hotel than current state, sync.
        // Both IDs must be defined to avoid false mismatches during initialization
        // or Auth0 silent-auth re-render cycles where myHotelInfo is temporarily null.
        if (storedHotelId && currentHotelId && storedHotelId !== currentHotelId) {
          logger.info(
            `HotelContext: Tab visible - hotel mismatch detected. Stored: ${storedHotelId}, Current: ${currentHotelId}`,
          );
          // Clear cache, reset notifications, and refresh data
          queryClient.clear();
          resetNotifications();
          fetchHotelInfo();
        }
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () =>
      document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, [
    myHotelInfo?.hotelId,
    isSwitching,
    getSelectedHotelId,
    queryClient,
    fetchHotelInfo,
    resetNotifications,
  ]);

  // Exposed refresh functions
  const refreshHotelInfo = useCallback(async (): Promise<MyHotelInfo | null> => {
    return await fetchHotelInfo();
  }, [fetchHotelInfo]);

  const refreshAvailableHotels = useCallback(async (): Promise<HotelAccessDto[]> => {
    return await fetchAvailableHotels();
  }, [fetchAvailableHotels]);

  const refreshRegistrationStatus = useCallback(async (): Promise<RegistrationStatusDto | null> => {
    return await fetchRegistrationStatus();
  }, [fetchRegistrationStatus]);

  const contextValue: HotelContextType = {
    myHotelInfo,
    availableHotels,
    isHotelInfoLoading,
    hasCompletedInitialFetch,
    isSwitching,
    switchStage,
    hasMultipleHotels: availableHotels.length > 1,
    isPlatformAdmin: myHotelInfo?.isPlatformAdmin ?? false,
    registrationStatus,
    fetchError,
    isAuthError,
    refreshHotelInfo,
    refreshAvailableHotels,
    refreshRegistrationStatus,
    selectHotel,
    searchAllHotels,
    getSelectedHotelId,
  };

  return (
    <HotelContext.Provider value={contextValue}>
      {children}
    </HotelContext.Provider>
  );
};
